import * as React from 'react';
import { RaRecord, useRecordContext } from 'react-admin';
import { formatDateTime, dayjs } from '../../ex_dayjs';

/** Record shape required by {@link DateTimeDiffFormatter}: any record with `dateTime`/`smDateTime` fields. */
interface DateTimeDiffRecord extends RaRecord {
    dateTime?: string | null;
    smDateTime?: string | null;
}

/** Props for {@link DateTimeDiffFormatter}. */
interface DateTimeDiffFormatterProps {
    /** Field on the record to render (formatted as a date/time). */
    source: string;
    /** Record to read from. Falls back to the surrounding `RecordContext` when omitted. */
    record?: DateTimeDiffRecord;
}

/**
 * Renders `record[source]` as a formatted date/time, colored to flag how far it drifts from
 * `record.smDateTime` compared to `record.dateTime`:
 * - No color when both dates are the same day and same minute.
 * - Orange when same day but within a 1-hour range.
 * - Red otherwise.
 *
 * @example
 * <DateTimeDiffFormatter source="dateTime" />
 */
const DateTimeDiffFormatter: React.FC<DateTimeDiffFormatterProps> = ({ source, record, ...props }) => {
    const _record = record || useRecordContext<DateTimeDiffRecord>(props) as DateTimeDiffRecord;
    let cellColor: string | undefined;
    if (_record.dateTime && _record.smDateTime) {
        let _date = dayjs(_record.dateTime);
        let _smdate = dayjs(_record.smDateTime);
        let isSameDay = _date.isSame(_smdate, 'day');
        let isSameTime = _date.isSame(_smdate, 'hour') && _date.isSame(_smdate, 'minute');
        let validRange = Math.abs(_date.diff(_smdate, 'hour')) === 1;

        cellColor = (isSameDay && isSameTime) ? '' : ((isSameDay && validRange) ? 'orange' : 'red');
    }

    return (
        <span style={{ backgroundColor: cellColor }}>
            {formatDateTime(_record[source])}
        </span>
    );
};

export default DateTimeDiffFormatter;
